"use client";

import {
  ActionBarMorePrimitive,
  ActionBarPrimitive,
  type AssistantState,
  AuiIf,
  BranchPickerPrimitive,
  ComposerPrimitive,
  groupPartByType,
  MessagePrimitive,
  SuggestionPrimitive,
  ThreadPrimitive,
  type ToolCallMessagePartComponent,
  useAui,
  useAuiEvent,
  useAuiState,
} from "@assistant-ui/react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  DownloadIcon,
  MicIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PinIcon,
  RefreshCwIcon,
  SquareIcon,
} from "lucide-react";
import {
  type ClipboardEvent,
  type ComponentType,
  createContext,
  type FC,
  type KeyboardEvent,
  type PropsWithChildren,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button } from "@/components/ui/atoms/button";
import { DotMatrix } from "@/components/ui/atoms/dot-matrix";
import { useComposerDraft } from "@/hooks/use-composer-draft";
import { useWorkspace } from "@/hooks/use-workspace";
import { getSavedModel } from "@/lib/composer-prefs";
import { answerFromMessage } from "@/lib/home/pin";
import { PAGE_PART, QUOTE_PART } from "@/lib/page-context";
import { isLargePaste, pastedTextFile } from "@/lib/paste";
import { lastPrompt, shouldRecall } from "@/lib/prompt-recall";
import { getHistory } from "@/lib/search-history";
import { isMacPlatform, newChatShortcutKeys } from "@/lib/shortcuts";
import { cn } from "@/lib/utils";
import { dispatchSearch } from "@/routes/dashboard/components/assistant/search-engine-picker";
import {
  BarGoButton,
  BarModeChip,
  openBarTarget,
  useBarMode,
} from "@/routes/dashboard/components/home/bar-mode";
import { usePinToHome } from "@/routes/dashboard/components/home/board/pin-to-home";
import { ComposerAddAttachment, ComposerAttachments, UserMessageAttachments } from "./attachment";
import { MessageSources } from "./citations";
import { ComposerQueue } from "./composer-queue";
import { MarkdownText } from "./markdown-text";
import { MessageError } from "./message-error";
import { MessageTiming } from "./message-timing";
import { Reasoning, ThinkingGroup } from "./reasoning";
import { QuotePart, SharedPagePart } from "./shared-context-parts";
import { ToolFallback } from "./tool-fallback";
import { ToolSteps } from "./tool-group";
import { TooltipIconButton } from "./tooltip-icon-button";
import { VisualAnswerToolUI } from "./visual-answer";
import { WebSearchToolUI } from "./web-search-tool";

type ThreadGroupPart = MessagePrimitive.GroupedParts.GroupPart;

/**
 * Optional component overrides for the thread. `AssistantMessage` and
 * `Welcome` replace whole sections; the remaining slots override how the
 * assistant message renders tool calls and part groups. Tool UIs registered
 * by name (toolkit `render`, `useAssistantDataUI`) take precedence over
 * `ToolFallback`.
 */
type ThreadComponents = {
  AssistantMessage?: ComponentType | undefined;
  Welcome?: ComponentType | undefined;
  /** Rendered under the composer while the thread is still empty (the console home). */
  NewChatFooter?: ComponentType | undefined;
  ToolFallback?: ToolCallMessagePartComponent | undefined;
  ToolGroup?: ComponentType<PropsWithChildren<{ group: ThreadGroupPart }>> | undefined;
  ReasoningGroup?: ComponentType<PropsWithChildren<{ group: ThreadGroupPart }>> | undefined;
};

export type ThreadProps = {
  components?: ThreadComponents | undefined;
  composerExtras?: ReactNode;
  /** Shown inside the composer, above the input (the side panel's page and tab chips). */
  composerHeader?: ReactNode;
  /**
   * `page`: the new tab, with the address row and web search shortcuts. `panel`: the side panel,
   * narrow, chat only.
   */
  variant?: "page" | "panel";
  /**
   * The column's width while the thread is still a new chat (the home board widens it once it
   * has more than Recent). Chats always read at 760px.
   */
  newChatMaxWidth?: string;
};

type ThreadLayout = { composerHeader?: ReactNode; variant: "page" | "panel" };
const ThreadLayoutContext = createContext<ThreadLayout>({ variant: "page" });

const EMPTY_COMPONENTS: ThreadComponents = {};

const ThreadComponentsContext = createContext<ThreadComponents>(EMPTY_COMPONENTS);

// Startup exposes a loading placeholder thread; treat it as a new chat so
// the composer mounts centered. Loads after startup keep the docked layout.
const isNewChatView = (s: AssistantState) =>
  s.thread.messages.length === 0 && (!s.thread.isLoading || s.threads.isLoading);

export const Thread: FC<ThreadProps> = ({
  components = EMPTY_COMPONENTS,
  composerExtras,
  composerHeader,
  variant = "page",
  newChatMaxWidth,
}) => {
  const isEmpty = useAuiState(isNewChatView);
  const layout = useMemo(() => ({ composerHeader, variant }), [composerHeader, variant]);

  return (
    <ThreadLayoutContext.Provider value={layout}>
      <ThreadComponentsContext.Provider value={components}>
        <ThreadRoot
          isEmpty={isEmpty}
          composerExtras={composerExtras}
          maxWidth={isEmpty ? newChatMaxWidth : undefined}
        />
      </ThreadComponentsContext.Provider>
    </ThreadLayoutContext.Provider>
  );
};

const ThreadRoot: FC<{ isEmpty: boolean; composerExtras?: ReactNode; maxWidth?: string }> = ({
  isEmpty,
  composerExtras,
  maxWidth,
}) => {
  const { Welcome = ThreadWelcome, NewChatFooter } = useContext(ThreadComponentsContext);
  const { variant } = useContext(ThreadLayoutContext);
  const panel = variant === "panel";

  return (
    <ThreadPrimitive.Root
      className="aui-root aui-thread-root bg-background @container flex h-full flex-col"
      style={{
        ["--thread-max-width" as string]: panel ? "100%" : (maxWidth ?? "47.5rem"),
        ["--composer-bg" as string]: "var(--color-card)",
        ["--composer-radius" as string]: "0",
        ["--composer-padding" as string]: "8px",
      }}
    >
      <ThreadPrimitive.Viewport
        turnAnchor="top"
        data-slot="aui_thread-viewport"
        className="relative flex flex-1 flex-col overflow-x-auto overflow-y-scroll scroll-smooth"
      >
        <div
          className={cn(
            "mx-auto flex w-full max-w-(--thread-max-width) flex-1 flex-col pt-4",
            panel ? "px-3" : "px-4",
            // A fixed offset, not centering, so the bar stays put as the list under it changes.
            // The side panel has no bar and centers its empty state.
            isEmpty && (panel ? "justify-center" : "pt-[18vh]"),
          )}
        >
          <AuiIf condition={isNewChatView}>
            <Welcome />
          </AuiIf>

          <div data-slot="aui_message-group" className="mb-14 flex flex-col gap-y-6 empty:hidden">
            <ThreadPrimitive.Messages>{() => <ThreadMessage />}</ThreadPrimitive.Messages>
          </div>

          <ThreadPrimitive.ViewportFooter
            className={cn(
              "aui-thread-viewport-footer bg-background flex flex-col gap-4 overflow-visible",
              panel ? "pb-3" : "pb-4 md:pb-6",
              !isEmpty && "sticky bottom-0 mt-auto rounded-t-(--composer-radius)",
            )}
          >
            <ThreadScrollToBottom />
            <Composer composerExtras={composerExtras} />
            {!panel && <SearchShortcutHints />}
            <AuiIf condition={(s) => isNewChatView(s) && s.composer.isEmpty}>
              <ThreadSuggestions />
            </AuiIf>
            {NewChatFooter && (
              <AuiIf condition={isNewChatView}>
                <NewChatFooter />
              </AuiIf>
            )}
          </ThreadPrimitive.ViewportFooter>
        </div>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
};

const ThreadMessage: FC = () => {
  const { AssistantMessage: AssistantMessageComponent = AssistantMessage } =
    useContext(ThreadComponentsContext);
  const role = useAuiState((s) => s.message.role);
  const isEditing = useAuiState((s) => s.message.composer.isEditing);

  if (isEditing) return <EditComposer />;
  if (role === "user") return <UserMessage />;
  return <AssistantMessageComponent />;
};

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip="Scroll to bottom"
        variant="outline"
        className="aui-thread-scroll-to-bottom dark:border-border dark:bg-background dark:hover:bg-accent absolute -top-12 z-10 self-center rounded-full p-4 disabled:invisible"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadWelcome: FC = () => {
  return (
    <div className="aui-thread-welcome-root mb-6 flex flex-col items-center px-4 text-center">
      <h1 className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in fill-mode-both text-2xl font-semibold duration-200">
        How can I help you today?
      </h1>
    </div>
  );
};

const ThreadSuggestions: FC = () => {
  return (
    <div className="aui-thread-welcome-suggestions flex w-full flex-wrap items-center justify-center gap-2 px-4">
      <ThreadPrimitive.Suggestions>{() => <ThreadSuggestionItem />}</ThreadPrimitive.Suggestions>
    </div>
  );
};

const ThreadSuggestionItem: FC = () => {
  return (
    <div className="aui-thread-welcome-suggestion-display fade-in slide-in-from-bottom-2 animate-in fill-mode-both duration-200">
      <SuggestionPrimitive.Trigger send asChild>
        <Button
          variant="ghost"
          className="aui-thread-welcome-suggestion text-foreground hover:bg-muted border-border/60 h-auto gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-normal whitespace-nowrap transition-colors"
        >
          <SuggestionPrimitive.Title className="aui-thread-welcome-suggestion-text-1" />
          <SuggestionPrimitive.Description className="aui-thread-welcome-suggestion-text-2 empty:hidden" />
        </Button>
      </SuggestionPrimitive.Trigger>
    </div>
  );
};

const SearchShortcutHints: FC = () => {
  const isMac = isMacPlatform();
  const modKey = isMac ? "⌘" : "Ctrl";
  const isNewChat = useAuiState(isNewChatView);

  return (
    <div className="text-muted-foreground hidden items-center justify-center gap-3 pt-1 text-[11px] sm:flex">
      <span>
        <kbd className="border-border border px-1 text-[11px]">Enter</kbd>
        <span className="ml-1">{isNewChat ? "go" : "ask AI"}</span>
      </span>
      {isNewChat && (
        <>
          <span className="text-muted-foreground/40">|</span>
          <span>
            <kbd className="border-border border px-1 text-[11px]">Tab</kbd>
            <span className="ml-1">open, search or ask</span>
          </span>
        </>
      )}
      <span className="text-muted-foreground/40">|</span>
      <span>
        <kbd className="border-border border px-1 text-[11px]">{modKey}</kbd>
        <kbd className="border-border border px-1 text-[11px] ml-0.5">Enter</kbd>
        <span className="ml-1">search web</span>
      </span>
      <span className="text-muted-foreground/40">|</span>
      <span>
        {newChatShortcutKeys(isMac).map((keys, n) => (
          <span key={keys.join("+")}>
            {n > 0 && <span className="mx-1">/</span>}
            {keys.map((key, i) => (
              <kbd
                key={key}
                className={cn("border-border border px-1 text-[11px]", i > 0 && "ml-0.5")}
              >
                {key}
              </kbd>
            ))}
          </span>
        ))}
        <span className="ml-1">new chat</span>
      </span>
    </div>
  );
};

const Composer: FC<{ composerExtras?: ReactNode }> = ({ composerExtras }) => {
  const aui = useAui();
  const hasMessages = useAuiState((s) => s.thread.messages.length > 0);
  const text = useAuiState((s) => s.composer.text);
  const { activeOrganizationId } = useWorkspace();
  const { variant, composerHeader } = useContext(ThreadLayoutContext);
  const panel = variant === "panel";
  // A new chat is the new tab's one bar: it opens URLs and searches as well as asks.
  // Replies in a chat, and the side panel, always ask.
  const asksOnly = hasMessages || panel;
  const bar = useBarMode(asksOnly ? "" : text, true);
  const barMode = asksOnly ? null : bar.mode;
  useComposerDraft();
  const attachmentError = useAttachmentError();

  useEffect(() => {
    // Cmd/Ctrl+Enter searches the web from the new tab; the side panel only asks.
    if (panel) return;
    const handler = (e: Event) => {
      const ke = e as globalThis.KeyboardEvent;
      if (ke.key === "Enter" && (ke.metaKey || ke.ctrlKey)) {
        e.preventDefault();
        e.stopPropagation();
        const input = document.querySelector(
          'textarea[aria-label="Message input"]',
        ) as HTMLTextAreaElement | null;
        const text = input?.value ?? "";
        dispatchSearch(text, activeOrganizationId);
      }
    };
    document.addEventListener("keydown", handler, { capture: true });
    return () => document.removeEventListener("keydown", handler, { capture: true });
  }, [activeOrganizationId, panel]);

  const go = () => openBarTarget(bar.target, activeOrganizationId);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const thread = aui.thread().getState();
    const composer = aui.composer().getState();
    // While an answer streams, Enter queues the message for after it (send now from the queue).
    if (
      e.key === "Enter" &&
      !e.shiftKey &&
      !e.nativeEvent.isComposing &&
      !e.metaKey &&
      !e.ctrlKey &&
      thread.isRunning
    ) {
      e.preventDefault();
      if (!composer.isEmpty) aui.composer().send({ steer: false });
      return;
    }
    // Arrow-up on an empty composer brings back the last prompt.
    if (shouldRecall(e.nativeEvent, composer.text) && composer.attachments.length === 0) {
      const prompt = lastPrompt(thread.messages, getHistory(activeOrganizationId));
      if (prompt) {
        e.preventDefault();
        aui.composer().setText(prompt);
        return;
      }
    }
    if (!asksOnly) bar.onKeyDown(e);
  };

  // A large paste goes in as a text attachment rather than filling the composer.
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (e.clipboardData.files.length > 0) return;
    const pasted = e.clipboardData.getData("text/plain");
    if (!isLargePaste(pasted)) return;
    e.preventDefault();
    const names = aui
      .composer()
      .getState()
      .attachments.map((a) => a.name);
    void aui.composer().addAttachment(pastedTextFile(pasted, names));
  };

  return (
    <ComposerPrimitive.Root
      className="aui-composer-root relative flex w-full flex-col"
      onSubmit={(e) => {
        if (barMode === "url" || barMode === "search") {
          e.preventDefault();
          go();
        }
      }}
    >
      <ComposerQueue />
      <ComposerPrimitive.AttachmentDropzone asChild>
        <div
          data-slot="aui_composer-shell"
          className="border-foreground data-[dragging=true]:border-ring focus-within:border-ring flex w-full flex-col gap-2 overflow-hidden rounded-(--composer-radius) border bg-(--composer-bg) p-(--composer-padding) transition-colors data-[dragging=true]:border-dashed data-[dragging=true]:bg-accent"
        >
          {composerHeader}
          <ComposerAttachments />
          {attachmentError && (
            <p role="alert" className="text-destructive px-1.5 text-xs">
              {attachmentError}
            </p>
          )}
          <ComposerPrimitive.Input
            placeholder={
              hasMessages ? "Reply" : panel ? "Ask anything" : "Search, type a URL, or ask anything"
            }
            className="aui-composer-input placeholder:text-muted-foreground max-h-32 min-h-10 w-full resize-none bg-transparent px-1.5 py-1 text-base leading-6 outline-none"
            rows={hasMessages || panel ? 1 : 2}
            autoFocus
            enterKeyHint={barMode === "url" ? "go" : barMode === "search" ? "search" : "send"}
            aria-label="Message input"
            onKeyDown={onKeyDown}
            onPaste={onPaste}
          />
          <ComposerAction
            composerExtras={composerExtras}
            modeChip={
              barMode && (
                <BarModeChip mode={barMode} canCycle={bar.modes.length > 1} onCycle={bar.cycle} />
              )
            }
            goButton={
              (barMode === "url" || barMode === "search") && (
                <BarGoButton mode={barMode} onClick={go} />
              )
            }
          />
        </div>
      </ComposerPrimitive.AttachmentDropzone>
    </ComposerPrimitive.Root>
  );
};

/** The last attachment the composer refused (too large, not a type the model reads), for 6 s. */
function useAttachmentError(): string | null {
  const [error, setError] = useState<string | null>(null);
  useAuiEvent("composer.attachmentAddError", (e) => {
    setError(
      e.reason === "not-accepted" ? "Kourai can read images, PDFs and text files." : e.message,
    );
  });
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 6000);
    return () => clearTimeout(timer);
  }, [error]);
  return error;
}

const ComposerAction: FC<{
  composerExtras?: ReactNode;
  /** What Enter does in the new tab bar (open, search or ask). */
  modeChip?: ReactNode;
  /** Replaces send while the bar opens a URL or searches. */
  goButton?: ReactNode;
}> = ({ composerExtras, modeChip, goButton }) => {
  return (
    <div className="aui-composer-action-wrapper relative flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-1">
        <ComposerAddAttachment />
        {composerExtras}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <AuiIf condition={(s) => s.thread.capabilities.dictation}>
          <AuiIf condition={(s) => s.composer.dictation == null}>
            <ComposerPrimitive.Dictate asChild>
              <TooltipIconButton
                tooltip="Voice input"
                side="bottom"
                type="button"
                variant="ghost"
                size="icon"
                className="aui-composer-dictate size-7 rounded-full"
                aria-label="Start voice input"
              >
                <MicIcon className="aui-composer-dictate-icon size-4" />
              </TooltipIconButton>
            </ComposerPrimitive.Dictate>
          </AuiIf>
          <AuiIf condition={(s) => s.composer.dictation != null}>
            <ComposerPrimitive.StopDictation asChild>
              <TooltipIconButton
                tooltip="Stop dictation"
                side="bottom"
                type="button"
                variant="ghost"
                size="icon"
                className="aui-composer-stop-dictation text-destructive size-7 rounded-full"
                aria-label="Stop voice input"
              >
                <SquareIcon className="aui-composer-stop-dictation-icon size-3.5 animate-pulse fill-current" />
              </TooltipIconButton>
            </ComposerPrimitive.StopDictation>
          </AuiIf>
        </AuiIf>
        {modeChip}
        {goButton || (
          <AuiIf condition={(s) => !s.thread.isRunning}>
            <ComposerPrimitive.Send asChild>
              <TooltipIconButton
                tooltip="Send message"
                side="bottom"
                type="button"
                variant="default"
                size="icon"
                className="aui-composer-send hover:bg-brand hover:text-brand-foreground size-8"
                aria-label="Send message"
              >
                <ArrowUpIcon className="aui-composer-send-icon size-4.5" />
              </TooltipIconButton>
            </ComposerPrimitive.Send>
          </AuiIf>
        )}
        <AuiIf condition={(s) => s.thread.isRunning && !s.composer.isEmpty}>
          <QueueSendButton />
        </AuiIf>
        <AuiIf condition={(s) => s.thread.isRunning}>
          <ComposerPrimitive.Cancel asChild>
            <Button
              type="button"
              variant="default"
              size="icon"
              className="aui-composer-cancel size-8"
              aria-label="Stop generating"
            >
              <SquareIcon className="aui-composer-cancel-icon size-3.5 fill-current" />
            </Button>
          </ComposerPrimitive.Cancel>
        </AuiIf>
      </div>
    </div>
  );
};

/** While an answer streams, send queues the message for after it. */
const QueueSendButton: FC = () => {
  const aui = useAui();
  return (
    <TooltipIconButton
      tooltip="Queue message"
      side="bottom"
      type="button"
      variant="outline"
      size="icon"
      className="aui-composer-queue size-8"
      aria-label="Queue message"
      onClick={() => aui.composer().send({ steer: false })}
    >
      <ArrowUpIcon className="size-4.5" />
    </TooltipIconButton>
  );
};

const AssistantMessage: FC = () => {
  const {
    ToolFallback: ToolFallbackComponent = ToolFallback,
    ToolGroup,
    ReasoningGroup,
  } = useContext(ThreadComponentsContext);

  // reserves space for action bar and compensates with `-mb` for consistent msg spacing
  // keeps hovered action bar from shifting layout (autohide doesn't support absolute positioning well)
  // for pt-[n] use -mb-[n + 6] & min-h-[n + 6] to preserve compensation
  const ACTION_BAR_PT = "pt-1.5";
  const ACTION_BAR_HEIGHT = `-mb-7.5 min-h-7.5 ${ACTION_BAR_PT}`;

  return (
    <MessagePrimitive.Root
      data-slot="aui_assistant-message-root"
      data-role="assistant"
      className="fade-in slide-in-from-bottom-1 animate-in relative duration-150"
    >
      <div
        data-slot="aui_assistant-message-content"
        // [contain-intrinsic-size:auto_24px] fixes issue #4104, don't change without checking for regressions
        className="text-foreground px-2 leading-relaxed wrap-break-word [contain-intrinsic-size:auto_24px] [content-visibility:auto]"
      >
        <MessagePrimitive.GroupedParts
          groupBy={groupPartByType({
            reasoning: ["group-chainOfThought", "group-reasoning"],
            "tool-call": ["group-chainOfThought", "group-tool"],
            // Tables and charts sit in the answer, not with the steps before it.
            "tool-call:show_visual": [],
            "standalone-tool-call": [],
          })}
        >
          {({ part, children }) => {
            switch (part.type) {
              case "group-chainOfThought":
                // Thinking and tool steps, before the answer.
                return (
                  <div data-slot="aui_chain-of-thought" className="mb-3 flex flex-col">
                    {children}
                  </div>
                );
              case "group-tool":
                if (ToolGroup) {
                  return <ToolGroup group={part}>{children}</ToolGroup>;
                }
                return (
                  <ToolSteps indices={part.indices} running={part.status.type === "running"}>
                    {children}
                  </ToolSteps>
                );
              case "group-reasoning":
                if (ReasoningGroup) {
                  return <ReasoningGroup group={part}>{children}</ReasoningGroup>;
                }
                return (
                  <ThinkingGroup indices={part.indices} running={part.status.type === "running"}>
                    {children}
                  </ThinkingGroup>
                );
              case "text":
                return <MarkdownText />;
              case "reasoning":
                return <Reasoning {...part} />;
              case "tool-call":
                if (part.toolUI) return part.toolUI;
                if (part.toolName === "show_visual") return <VisualAnswerToolUI {...part} />;
                if (part.toolName === "web_search" || part.toolName === "fetch_page") {
                  return <WebSearchToolUI {...part} />;
                }
                return <ToolFallbackComponent {...part} />;
              case "data":
                return part.dataRendererUI;
              case "indicator":
                return (
                  <span
                    role="status"
                    data-slot="aui_assistant-message-indicator"
                    className="text-muted-foreground inline-flex items-center gap-2 align-middle"
                    aria-label="Assistant is working"
                  >
                    <DotMatrix state="connecting" className="size-4" />
                    <span className="text-sm">Connecting</span>
                  </span>
                );
              default:
                return null;
            }
          }}
        </MessagePrimitive.GroupedParts>
        <MessageError />
      </div>

      <div
        data-slot="aui_assistant-message-footer"
        className={cn("ms-2 flex items-center", ACTION_BAR_HEIGHT)}
      >
        <MessageSources />
        <BranchPicker />
        <AssistantActionBar />
      </div>
    </MessagePrimitive.Root>
  );
};

const AssistantActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-assistant-action-bar-root text-muted-foreground animate-in fade-in col-start-3 row-start-2 -ms-1 flex gap-1 duration-200"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip="Copy">
          <AuiIf condition={(s) => s.message.isCopied}>
            <CheckIcon className="animate-in zoom-in-50 fade-in duration-200 ease-out" />
          </AuiIf>
          <AuiIf condition={(s) => !s.message.isCopied}>
            <CopyIcon className="animate-in zoom-in-75 fade-in duration-150" />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <TooltipIconButton tooltip="Refresh">
          <RefreshCwIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Reload>
      <PinToHomeButton />
      <MessageTiming />
      <ActionBarMorePrimitive.Root>
        <ActionBarMorePrimitive.Trigger asChild>
          <TooltipIconButton tooltip="More" className="data-[state=open]:bg-accent">
            <MoreHorizontalIcon />
          </TooltipIconButton>
        </ActionBarMorePrimitive.Trigger>
        <ActionBarMorePrimitive.Content
          side="bottom"
          align="start"
          sideOffset={6}
          className="aui-action-bar-more-content bg-popover/95 text-popover-foreground data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:animate-out data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] overflow-hidden rounded-xl border p-1.5 shadow-lg backdrop-blur-sm"
        >
          <ActionBarPrimitive.ExportMarkdown asChild>
            <ActionBarMorePrimitive.Item className="aui-action-bar-more-item hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm outline-none select-none">
              <DownloadIcon className="size-4" />
              Export as Markdown
            </ActionBarMorePrimitive.Item>
          </ActionBarPrimitive.ExportMarkdown>
        </ActionBarMorePrimitive.Content>
      </ActionBarMorePrimitive.Root>
    </ActionBarPrimitive.Root>
  );
};

/** The question that asked this answer: the user message before it, as text. */
function useQuestion() {
  return useAuiState((s) => {
    const messages = s.thread.messages;
    const at = messages.findIndex((m) => m.id === s.message.id);
    for (let i = at - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.role !== "user") continue;
      return m.content
        .map((p) => (p.type === "text" ? p.text : ""))
        .join(" ")
        .trim();
    }
    return "";
  });
}

/** "Pin to home" (ADMIN-266): keeps this answer on the board as a widget. */
const PinToHomeButton: FC = () => {
  const pin = usePinToHome();
  const parts = useAuiState((s) => s.message.parts);
  const question = useQuestion();
  const [state, setState] = useState<"idle" | "pinning" | "pinned" | "failed">("idle");
  useEffect(() => {
    if (state !== "pinned" && state !== "failed") return;
    const t = setTimeout(() => setState("idle"), 2500);
    return () => clearTimeout(t);
  }, [state]);
  if (!pin) return null;
  const answer = answerFromMessage(parts as Parameters<typeof answerFromMessage>[0], question);
  if (!answer) return null;
  return (
    <TooltipIconButton
      tooltip={
        state === "pinned"
          ? "Pinned to your home"
          : state === "failed"
            ? "Couldn't pin it. Try again."
            : "Pin to home"
      }
      disabled={state === "pinning"}
      onClick={async () => {
        setState("pinning");
        setState((await pin(answer, getSavedModel())) ? "pinned" : "failed");
      }}
    >
      {state === "pinned" ? (
        <CheckIcon className="animate-in zoom-in-50 fade-in duration-200 ease-out" />
      ) : (
        <PinIcon />
      )}
    </TooltipIconButton>
  );
};

/** Pages and quotes attached from the side panel (data parts, see lib/page-context.ts). */
const USER_PART_COMPONENTS = {
  data: { by_name: { [PAGE_PART]: SharedPagePart, [QUOTE_PART]: QuotePart } },
};

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      data-slot="aui_user-message-root"
      className="fade-in slide-in-from-bottom-1 animate-in grid auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 duration-150 [contain-intrinsic-size:auto_60px] [content-visibility:auto] [&:where(>*)]:col-start-2"
      data-role="user"
    >
      <UserMessageAttachments />

      <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
        <div className="aui-user-message-content peer bg-muted text-foreground rounded-xl px-4 py-2 wrap-break-word empty:hidden">
          <MessagePrimitive.Parts components={USER_PART_COMPONENTS} />
        </div>
        <div className="aui-user-action-bar-wrapper absolute start-0 top-1/2 -translate-x-full -translate-y-1/2 pe-2 peer-empty:hidden rtl:translate-x-full">
          <UserActionBar />
        </div>
      </div>

      <BranchPicker
        data-slot="aui_user-branch-picker"
        className="col-span-full col-start-1 row-start-3 -me-1 justify-end"
      />
    </MessagePrimitive.Root>
  );
};

const UserActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-user-action-bar-root flex flex-col items-end"
    >
      <ActionBarPrimitive.Edit asChild>
        <TooltipIconButton tooltip="Edit" className="aui-user-action-edit">
          <PencilIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Edit>
    </ActionBarPrimitive.Root>
  );
};

const EditComposer: FC = () => {
  return (
    <MessagePrimitive.Root data-slot="aui_edit-composer-wrapper" className="flex flex-col px-2">
      <ComposerPrimitive.Root className="aui-edit-composer-root border-border/60 dark:border-muted-foreground/15 ms-auto flex w-full max-w-[85%] flex-col rounded-(--composer-radius) border bg-(--composer-bg) shadow-[0_4px_16px_-8px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-none">
        <ComposerPrimitive.Input
          className="aui-edit-composer-input text-foreground min-h-14 w-full resize-none bg-transparent px-4 pt-3 pb-1 text-base outline-none"
          autoFocus
        />
        <div className="aui-edit-composer-footer mx-2.5 mb-2.5 flex items-center gap-1.5 self-end">
          <ComposerPrimitive.Cancel asChild>
            <Button variant="ghost" size="sm" className="h-8 rounded-full px-3.5">
              Cancel
            </Button>
          </ComposerPrimitive.Cancel>
          <ComposerPrimitive.Send asChild>
            <Button size="sm" className="h-8 rounded-full px-3.5">
              Update
            </Button>
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
    </MessagePrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({ className, ...rest }) => {
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        "aui-branch-picker-root text-muted-foreground -ms-2 me-2 inline-flex items-center text-xs",
        className,
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip="Previous">
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip="Next">
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
